import { ensureNotNil, isNil } from "client/Misc/utils";
import {
	DateTime,
	Duration,
	DurationUnits
} from "luxon";
import { DayOfWeek } from "./culture";
import { TimeZone } from "./timezones";

export type TimeUnit = "millisecond" | "second" | "minute" | "hour";

export function getStartOfDay(
	dateTime: Date,
	timezone: TimeZone
): Date {
	return DateTime.fromJSDate(dateTime, { zone: timezone.identifier })
		.startOf("day")
		.toJSDate();
}

export function getStartOfWeek(
	dateTime: Date,
	timezone: TimeZone
): Date {
	return DateTime.fromJSDate(dateTime, { zone: timezone.identifier })
		.startOf("week", { useLocaleWeeks: true })
		.toJSDate();
}

export function getStartOfIsoWeek(
	date: Date,
	timezone: TimeZone
): Date {
	return DateTime.fromJSDate(date, { zone: timezone.identifier })
		.startOf("week")
		.toJSDate();
}

export function getWeekNumber(
	date: Date,
	timezone: TimeZone
): number {
	return DateTime.fromJSDate(date, { zone: timezone.identifier })
		.localWeekNumber;
}

export function parseDtoDate(date: Date | string): Date;
export function parseDtoDate(date: Date | string | nil): Date | null;
export function parseDtoDate(date: Date | string | nil): Date | null {
	if (isNil(date)) {
		return null;
	}

	if (typeof date === "string") {
		return new Date(
			Date.parse(date)
		);
	}

	return date;
}

export function getIsoWeekNumber(
	date: Date,
	timezone: TimeZone
): number {
	return DateTime.fromJSDate(date, { zone: timezone.identifier })
		.weekNumber;
};

export function formatForDto(date: Date): string;
export function formatForDto(date: Date | nil): string | undefined;
export function formatForDto(date: Date, timezone: TimeZone): string;
export function formatForDto(date: Date | nil, timezone: TimeZone): string | undefined;
export function formatForDto(date: Date | nil, timezone: TimeZone | null = null): string | undefined {
	if (isNil(date)) {
		return undefined;
	}

	if (isNil(timezone)) {
		return date.toISOString();
	}

	return ensureNotNil(
		DateTime.fromJSDate(date, { zone: timezone.identifier })
			.toISO()
	);
}

export const millisecondsInASecond = 1000;
export const millisecondsInAMinute = millisecondsInASecond * 60;
export const millisecondsInAnHour = millisecondsInAMinute * 60;

export function addDays(
	date: Date,
	numberOfDays: number,
	timezone: TimeZone
): Date {
	return DateTime.fromJSDate(date, { zone: timezone.identifier })
		.plus({ days: numberOfDays })
		.toJSDate();
}

export function addTime(
	date: Date,
	value: number,
	unit: TimeUnit
): Date {
	return new Date(date.getTime() + value * timeUnitToNumberOfMillisecond[unit]);
}

export type DateRangeComparisonOptions = {
	shouldIncludeStart: boolean;
	shouldIncludeEnd: boolean;
};

export function isBetween(
	date: Date,
	start: Date,
	end: Date,
	options?: DateRangeComparisonOptions
) {
	const defaultOptions: DateRangeComparisonOptions = {
		shouldIncludeStart: true,
		shouldIncludeEnd: false
	};

	const {
		shouldIncludeStart,
		shouldIncludeEnd
	} = options ?? defaultOptions;

	const dateMs: number = date.getTime();
	const startMs: number = start.getTime();
	const endMs: number = end.getTime();

	const doesSatisfyStart: boolean = shouldIncludeStart
		? startMs <= dateMs
		: startMs < dateMs;

	const doesSatisfyEnd: boolean = shouldIncludeEnd
		? endMs >= dateMs
		: endMs > dateMs;

	return doesSatisfyStart && doesSatisfyEnd;
}

const timeUnitToNumberOfMillisecond: {[key in TimeUnit]: number} = {
	millisecond: 1,
	second: millisecondsInASecond,
	minute: millisecondsInAMinute,
	hour: millisecondsInAnHour
};

export function calculateTimeDifference(
	end: Date,
	start: Date,
	unit: TimeUnit = "millisecond",
	shouldIncludeFractions: boolean = false
): number {
	const result: number = (end.getTime() - start.getTime()) / timeUnitToNumberOfMillisecond[unit];

	if (shouldIncludeFractions) {
		return result;
	}

	return Math.floor(result);
}

export function calculateDayDifference(
	end: Date,
	start: Date,
	timezone: TimeZone,
	shouldIncludeFractions?: boolean
): number {
	const unit: DurationUnits = shouldIncludeFractions
		? "days"
		// Pass the fraction onto the hours difference
		: ["days", "hours"];

	const difference: Duration = DateTime.fromJSDate(end, { zone: timezone.identifier })
		.diff(
			DateTime.fromJSDate(start, { zone: timezone.identifier }),
			unit
		);

	return difference.days;
}

export function getDayOfWeek(
	date: Date,
	timezone: TimeZone
): DayOfWeek {
	const dayNumber: number = DateTime.fromJSDate(date, { zone: timezone.identifier })
		.weekday;

	return toDayOfWeek(dayNumber);
}

export function getDayOfMonth(
	date: Date,
	timezone: TimeZone
): number {
	return DateTime.fromJSDate(date, { zone: timezone.identifier })
		.day;
}

export function areOnTheSameDay(
	date1: Date,
	date2: Date,
	timezone: TimeZone
): boolean {
	return getStartOfDay(date1, timezone).getTime()
		=== getStartOfDay(date2, timezone).getTime();
}

export function getIsToday(
	date: Date,
	timezone: TimeZone,
	today?: Date
): boolean {
	today ??= getStartOfDay(
		new Date(),
		timezone
	);

	const tomorrow: Date = addDays(today, 1, timezone);

	return isBetween(
		date,
		today,
		tomorrow
	);
}

export function toDayOfWeek(luxonWeekDay: number): DayOfWeek {
	return luxonWeekDay % 7 as DayOfWeek;
}

export function getFullMonthName(date: Date, timezone: TimeZone): string {
	return DateTime.fromJSDate(date, { zone: timezone.identifier })
		.toLocaleString({ month: "long" });
};

export function isValidDate(value: Date | string | null): boolean {
	if (!value) {
		return true;
	}

	return !isNaN(parseDtoDate(value).getTime());
}
